import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Home,
  Mail,
  Play,
  Package,
  Box,
  Truck,
  MapPin,
  Wallet,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ShoppingCart,
  MessageCircle,
  Store
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import CartModal from './CartModal';
import AIChatbot from './AIChatbot';
import { authAPI } from '../lib/api';
import { getUserFromStorage } from '../utils/userDataMigration';

interface LayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  { path: '/', icon: Home, label: 'Dashboard' },
  { path: '/stores', icon: Store, label: 'Stores', roles: ['super_user', 'entity_admin'] },
  { path: '/users', icon: Users, label: 'Users', roles: ['super_user', 'entity_admin'] },
  { path: '/orders', icon: Mail, label: 'Orders' },
  { path: '/subscriptions', icon: Play, label: 'Subscriptions', roles: ['admin', 'super_user', 'entity_admin', 'customer'] },
  { path: '/products', icon: Package, label: 'Products', roles: ['admin', 'super_user', 'entity_admin', 'customer'] },
  { path: '/inventory', icon: Box, label: 'Inventory', roles: ['super_user', 'entity_admin', 'admin', 'staff'] },
  { path: '/delivery', icon: Truck, label: 'Delivery Routes', roles: ['super_user', 'entity_admin', 'admin', 'staff', 'delivery'] },
  { path: '/addresses', icon: MapPin, label: 'My Addresses', roles: ['admin', 'super_user', 'entity_admin', 'customer'] },
  { path: '/wallet', icon: Wallet, label: 'Wallet', roles: ['admin', 'super_user', 'entity_admin', 'customer'] },
  { path: '/customers', icon: Users, label: 'Customers', roles: ['super_user', 'entity_admin', 'admin', 'staff'] },
  { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['super_user', 'entity_admin', 'admin', 'staff'] },
  { path: '/settings', icon: Settings, label: 'Settings' },
  { path: '/help', icon: MessageCircle, label: 'Help & Support' },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const { getCartItemsCount, setIsCartOpen } = useCart();
  const cartCount = getCartItemsCount();

  useEffect(() => {
    const userData = getUserFromStorage();
    if (userData) {
      setUser(userData);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedOrg');
    navigate('/login');
  };

  const filteredMenuItems = menuItems.filter(item => {
    if (!user) return false;
    if (!item.roles) return true;
    return item.roles.includes(user.role);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-[#1e3a5f] text-white transform transition-transform duration-300 ease-in-out shadow-2xl ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 p-6 border-b border-white/5">
            <div className="w-10 h-10 bg-gradient-to-br from-[#10b981] to-[#059669] rounded-xl flex items-center justify-center shadow-lg shadow-[#10b981]/20">
              <ShoppingCart size={22} className="text-white" />
            </div>
            <div>
              <span className="text-xl font-bold tracking-tight block">GrocerySub</span>
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Admin Panel</span>
            </div>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1 custom-scrollbar">
            {filteredMenuItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${isActive
                    ? 'bg-[#10b981] text-white shadow-lg shadow-[#10b981]/20'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <Icon size={20} className={isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'} />
                  <span className="font-semibold">{item.label}</span>
                  {isActive && <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full" />}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 m-4 bg-white/5 rounded-2xl border border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#10b981] to-[#059669] rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {user?.fullName?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white truncate">{user?.fullName || user?.name || 'User'}</p>
                <p className="text-xs text-gray-400 truncate">{user?.email || ''}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white rounded-xl transition-all duration-200 font-bold text-sm"
            >
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-72 transition-all duration-300">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Logo for header */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#10b981] to-[#059669] rounded-xl flex items-center justify-center shadow-md shadow-[#10b981]/20">
                {user?.role === 'delivery' ? <Truck size={20} className="text-white" /> : <ShoppingCart size={20} className="text-white" />}
              </div>
              <div className="hidden sm:block">
                <h1 className="font-bold text-gray-900 text-lg leading-tight">Freshio</h1>
                <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Grocery Delivery</p>
              </div>
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-4">
              {user?.role === 'delivery' && (
                <div className="hidden sm:flex items-center gap-2 bg-gray-50 border border-gray-100 px-3 py-1.5 rounded-xl shadow-sm">
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Balance</span>
                  <span className="text-sm font-bold text-[#10b981]">₹{(user?.wallet?.balance || 0).toLocaleString()}</span>
                </div>
              )}

              {/* Cart Button */}
              {user?.role !== 'delivery' && (
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="relative p-2.5 text-gray-600 hover:bg-gray-100 rounded-xl transition-all hover:scale-105 active:scale-95"
                >
                  <ShoppingCart size={24} />
                  {cartCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold border-2 border-white">
                      {cartCount > 9 ? '9+' : cartCount}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 animate-fade-in">{children}</main>

      </div>

      {/* Cart Modal */}
      <CartModal />

      {/* Chatbot Button */}
      <button
        onClick={() => setShowChatbot(!showChatbot)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-[#10b981] to-[#059669] text-white rounded-full shadow-lg shadow-[#10b981]/30 hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center z-40 active:scale-95"
      >
        <MessageCircle size={24} />
      </button>

      {/* Chatbot */}
      {showChatbot && <AIChatbot onClose={() => setShowChatbot(false)} />}
    </div>
  );
};

export default Layout;